<template>
  <HgModal
    v-model="showModal"
    :closeButtonCssClass="$style.modalClose"
    :containerCssClass="$style.modalContainer"
    closeOnBackgroundClick
    closeOnEscapePressed
    transitionStyle="fade"
    @close="handleCloseModal"
  >
    <div :class="$style.container">
      <div :class="$style.header">
        <h3 :class="$style.title">{{ t('neighbourhood.insights.modal.title') }}</h3>
      </div>

      <div :class="$style.body">
        <div :class="$style.iconContainer">
          <img
            :src="constructionIcon"
            :alt="t('neighbourhood.insights.modal.title')"
            :class="$style.icon"
          />
        </div>

        <p :class="$style.intro">
          {{ t('neighbourhood.insights.modal.description') }}
        </p>
        <p :class="$style.intro">
          {{ t('neighbourhood.insights.modal.description2') }}
        </p>
      </div>

      <div :class="$style.footer">
        <HgButton
          :class="$style.ctaButton"
          data-test="neighbourhood-close-cta"
          @click="handleCloseModal"
        >
          {{ t('neighbourhood.insights.modal.close') }}
        </HgButton>
      </div>
    </div>
  </HgModal>
</template>

<script setup lang="ts">
import { HgButton, HgModal } from '@smg-real-estate/domus';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import constructionIcon from '@/assets/neighbourhood/construction_icon.svg?url';

export interface ExploreNearbyModalProps {
  isModalVisible: boolean;
}

export interface ExploreNearbyModalEmits {
  close: [];
}

const props = defineProps<ExploreNearbyModalProps>();
const emit = defineEmits<ExploreNearbyModalEmits>();

const { t } = useI18n();

const showModal = computed({
  get: () => props.isModalVisible,
  set: () => emit('close'),
});

const handleCloseModal = (): void => {
  emit('close');
};
</script>

<style lang="scss" module>
@import '@/assets/variables';

$modal-width: 320px;
$modal-height: 568px;

$body-width: 245px;
$body-height: 272px;

.modalContainer {
  width: $modal-width;
  height: $modal-height;
  padding: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modalClose {
  top: $space-25;
  padding: $space-75;
}

.container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.header {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  padding: $space-75 $space-150;
  border-bottom: 1px solid $color-grey-50;
  flex-shrink: 0;
}

.title {
  @include heading-3;
  padding-right: $space-150;
  margin: 0;
}

.body {
  flex: 1 1 auto;
  width: $body-width;
  height: $body-height;
  margin: 0 auto;
  padding: $space-150;
}

.iconContainer {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: $space-150 0 $space-100 0;
}

.icon {
  width: 128px;
  height: 128px;
}

.intro {
  @include body-2;
  margin: 0 0 $space-100 0;
  padding: 0 $space-50;
  color: $color-black;
  text-align: center;
  line-height: 1.6;
}

.footer {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  padding: $space-75 $space-150;
  border-top: 1px solid $color-grey-50;
  flex: 0 0 auto;
}

.ctaButton {
  width: auto;
  min-width: fit-content;
}
</style>
